import * as angular from 'angular';

import { IFileManagementService, IDataFolderService, ILoggerService, getMimeTypeFromExtension, PdfToSvgService } from '../../services';
import { DataFolderOrigin, PreviewMode } from '../../models/enum';
import { ComponentDefinition, Group, HierarchicalDirectory, IFile, IScript } from '../../models';

class FilePreviewController implements angular.IComponentController {
    content: string;
    disableLoading = false;
    fileContent: string | ArrayBuffer;
    fileMimeType: string;
    fileTooLarge: boolean;
    group: Group;
    isDirectory: boolean;
    isLoading: boolean;
    previewMode: PreviewMode;
    previewModes = PreviewMode;
    scriptCtrl: IScript;

    readonly applicationGuid: string;
    readonly connectionPointGuid: string;
    readonly file: IFile;
    readonly fileBase64: string;
    readonly fileName: string;
    // Render file directly from octet stream
    readonly isDirectRender: Boolean;
    readonly isReadOnly: boolean;

    static $inject = ['$sce', 'fileManagementService', 'dataFolderService', 'loggerService', 'pdfToSvgService'];

    constructor(
        private _$sce: angular.ISCEService,
        private _fileManagementService: IFileManagementService,
        private _dataFolderService: IDataFolderService,
        private _loggerService: ILoggerService,
        private _pdfToSvgService: PdfToSvgService
    ) {}

    $onInit() {
        const file = this.file || this.fileBase64;
        let isActive = false;
        if (!this.fileBase64) {
            isActive = file ? this._fileManagementService.getPreviewMode((file as IFile).name) === PreviewMode.Python : false;
        }
        this.scriptCtrl = {
            saveFile: () => this.saveFile(),
            active: isActive
        };
        this.group = { script: '' } as Group;
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['file'] && changesObj['file'].currentValue) {
            const file = changesObj['file'].currentValue;
            if (file) {
                this.render(file);
            }
        }
        if (changesObj['fileBase64'] && changesObj['fileBase64'].currentValue) {
            const fileBase64 = changesObj['fileBase64'].currentValue;
            if (fileBase64) {
                this.render(fileBase64);
            }
        }
    }

    private saveFile() {
        this.scriptCtrl.codeEditor.saveFile(this.getRelativeFileName());
    }

    codemirrorLoaded(e) {
        e.on('change', () => e.refresh());
    }

    private getRelativeFileName() {
        return `${this.file.parent.relativePath}\\${this.file.name}`;
    }

    private render(file: File) {
        let mode: PreviewMode;
        if (file && file.name) {
            mode = this._fileManagementService.getPreviewMode(file.name);
        }
        this.previewMode = PreviewMode.Unknown;
        this.fileContent = undefined;
        if (this.scriptCtrl) {
            this.scriptCtrl.active = mode === PreviewMode.Python;
        }
        this.previewMode = mode;

        if (file.size > 5242880) {
            this.fileTooLarge = true;
        } else {
            this.fileTooLarge = false;
            if (this.fileBase64) {
                this.imageRender(this.fileBase64);
            } else if (this.connectionPointGuid) {
                this.dataFolderRender(file, mode);
            } else {
                if (this.isDirectRender) {
                    this.fileRender(file);
                } else {
                    this.applicationRender(file);
                }
            }
        }
    }

    private imageRender(fileBase64: string) {
        this.previewMode = PreviewMode.Image;
        this.fileContent = fileBase64;
    }

    // Render a blob to text or image
    // If mime type is not supported, set mode as unknown
    private fileRender(file: Blob, fileName?: string) {
        this.fileMimeType = getMimeTypeFromExtension(fileName || this.fileName);
        if (window.FileReader && window.Blob) {
            const reader = new window.FileReader();
            if (this.fileMimeType === 'plain/text') {
                // Text file
                this.previewMode = PreviewMode.Text;
                reader.onloadend = () => {
                    this.fileContent = reader.result;
                };
                reader.readAsText(file);
            } else if (this.fileMimeType.substr(0, 5) === 'image') {
                // Image
                this.previewMode = PreviewMode.Object;
                reader.onloadend = () => {
                    this.fileContent = (reader.result as string).replace('data:application/octet-stream', 'data:' + this.fileMimeType);
                };
                reader.readAsDataURL(file);
            } else if (this.fileMimeType === 'application/pdf') {
                // Pdf file
                this.previewMode = this.connectionPointGuid ? PreviewMode.Pdf : PreviewMode.Object;
                reader.onloadend = (e) => {
                    const arraybuffer = reader.result as ArrayBuffer;
                    const uint8array = new Uint8Array(arraybuffer);
                    if (this.connectionPointGuid) {
                        const blob = new Blob([uint8array], { type: 'application/pdf' });
                        this.fileContent = this._$sce.trustAsResourceUrl(URL.createObjectURL(blob));
                    } else {
                        this._pdfToSvgService.convertBlobToBase64(uint8array).then((data) => {
                            this.fileContent = data;
                        });
                    }
                };
                reader.readAsArrayBuffer(file);
            } else {
                // Unsupported file
                this.previewMode = PreviewMode.Unknown;
            }
        }
    }

    private applicationRender(file: File | HierarchicalDirectory) {
        this.isDirectory = (file as HierarchicalDirectory).relativePath !== undefined;
        if (!this.isDirectory) {
            this.isLoading = true;
            const relativePath = (file as IFile).parent.relativePath;
            this._fileManagementService
                .getFileFromContentAsUrl(relativePath + '\\' + file.name, this.applicationGuid)
                .then((data) => (this.fileContent = data))
                .catch((reason) => console.error(reason))
                .finally(() => {
                    this.isLoading = false;
                    if (this.previewMode === PreviewMode.Python) {
                        this.group = { script: this.fileContent } as Group;
                    }
                });
        }
    }

    private dataFolderRender(file: string | File, mode: PreviewMode) {
        this.isLoading = true;
        const fileName = (file as File).name;
        this._dataFolderService
            .preview(this.connectionPointGuid, fileName)
            .then((data) => {
                this.fileRender(data, fileName);
            })
            .catch((reason) => {
                this._loggerService.logError(reason && reason.message ? reason.message : reason);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }
}

export const wtFilePreview: ComponentDefinition = {
    componentId: 'wtFilePreview',
    controller: FilePreviewController,
    template: require('./filePreview.component.html'),
    controllerAs: 'vm',
    bindings: {
        applicationGuid: '<?',
        connectionPointGuid: '<?',
        disableLoading: '@',
        file: '<',
        fileBase64: '<?',
        fileContent: '=?',
        fileName: '<?',
        isDirectRender: '@?',
        isLoading: '=?',
        isReadOnly: '<?',
        scriptCtrl: '=?',
    }
};
